import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import ModalPortal from '../../../../../../components/modal-portal';
import { InsuranceTermsModal } from '../../../../../../components/modals/insurance-terms-modal';
import { Providers } from '../../../../../../enums/providers';
import { useEnableUpsTerms2 } from '../../../../../../feature-flags/hooks/use-enable-ups-terms-2';
import { useFulfillmentPlannerContext } from '../../../../../../fulfillment-planner/context';
import { useHasUserAcceptedInsuranceTerms, useInsuranceTermsModal } from '../../../../../../services/insurance-terms-service';
import { hasUserAcceptedUpsLabelTerms, launchUpsLabelTermsModal } from '../../../../../../services/ups-label-terms-service';
import Global from '../../../../../../store/global-selectors';
import { checkUpsLabelTermsRequired } from '../../../../../../store/seller/seller.api';
import { CreateReturnLabelAction, useCreateReturnContext } from '../../../../context/create-return-provider';
import { LabelPurchaseButton } from '../../../../../../components/label-purchase-button';

import messages from './messages';
import style from './label-section-content.module.less';
import { EventSourceStrings } from '../../../../../../tracking/tracking-constants';

const CreateReturnLabelButton = ({ isDisabled = false }) => {
  const fulfillmentPlannerContext = useFulfillmentPlannerContext();
  const { insuranceProvider, serviceIdAndSellerLabelProviderId } = fulfillmentPlannerContext;

  const createReturnContext = useCreateReturnContext();
  const { selectedCreateLabelOption, createReturnLabel } = createReturnContext;

  const insuranceProviderId = insuranceProvider?.insuranceProviderId;

  const hasAcceptedInsuranceTerms = useHasUserAcceptedInsuranceTerms(insuranceProviderId);

  const isUpsLabelTermsV2Enabled = useEnableUpsTerms2();
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const sellerLabelProvider = sellerLabelProviders?.find(
    (slp) => slp.sellerLabelProviderId === serviceIdAndSellerLabelProviderId?.sellerLabelProviderId,
  );
  const isUpsWallet = sellerLabelProvider?.labelProviderId === Providers.UPSWallet.labelProviderId;
  const hasAcceptedUpsTerms = useSelector((state) => hasUserAcceptedUpsLabelTerms(state));
  const mightNeedUpsTermsAcceptance = isUpsLabelTermsV2Enabled && isUpsWallet && !hasAcceptedUpsTerms;

  const [isCheckingUpsTerms, setIsCheckingUpsTerms] = useState(false);

  const { isInsuranceTermsModalOpen, openInsuranceTermsModal, insuranceTermsModalModalFrameworkMethods, onAcceptTerms, onDeclineTerms } =
    useInsuranceTermsModal(insuranceProviderId, createReturnLabel);

  const messageDescriptor = selectedCreateLabelOption === CreateReturnLabelAction.Email ? messages.emailAction : messages.downloadAction;

  const proceedWithLabelCreation = useCallback(() => {
    if (hasAcceptedInsuranceTerms) {
      createReturnLabel();
    } else {
      openInsuranceTermsModal();
    }
  }, [createReturnLabel, hasAcceptedInsuranceTerms, openInsuranceTermsModal]);

  const onClickCreateReturnLabel = useCallback(async () => {
    if (mightNeedUpsTermsAcceptance) {
      setIsCheckingUpsTerms(true);
      try {
        const response = await checkUpsLabelTermsRequired();
        setIsCheckingUpsTerms(false);
        if (response.termsRequired) {
          launchUpsLabelTermsModal(proceedWithLabelCreation);
        } else {
          proceedWithLabelCreation();
        }
      } catch {
        setIsCheckingUpsTerms(false);
        // On error, show modal to be safe
        launchUpsLabelTermsModal(proceedWithLabelCreation);
      }
      return;
    }
    proceedWithLabelCreation();
  }, [mightNeedUpsTermsAcceptance, proceedWithLabelCreation]);

  return (
    <>
      <LabelPurchaseButton labelPurchaseCallback={onClickCreateReturnLabel} trackingId={EventSourceStrings.RETURN_LABEL_BUTTON}>
        {({ onClick }) => (
          <Button
            className={style['create-return-label-button']}
            disabled={isDisabled || isCheckingUpsTerms}
            onClick={onClick}
            theme={THEME_CONSTANTS.primary}
          >
            <FormattedMessage {...messageDescriptor} />
          </Button>
        )}
      </LabelPurchaseButton>
      {isInsuranceTermsModalOpen && (
        <ModalPortal onClose={onDeclineTerms}>
          <InsuranceTermsModal
            insuranceProviderId={insuranceProviderId}
            modalFrameworkMethods={insuranceTermsModalModalFrameworkMethods}
            onAcceptTerms={onAcceptTerms}
          />
        </ModalPortal>
      )}
    </>
  );
};

CreateReturnLabelButton.propTypes = {
  isDisabled: PropTypes.bool,
};

export default CreateReturnLabelButton;
